import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["hs_action_metadata", "hs_all_assigned_business_unit_ids", "hs_attribution_enabled", "hs_audience", "hs_ai_features", "hs_budget_amount", "hs_budget_items_sum_amount", "hs_budget", "hs_brief_properties", "hs_campaign_status", "hs_color_hex", "hs_created_at", "hs_created_by_user_id", "hs_currency_code", "hs_end_date", "hs_goal", "hs_goal_intention", "hs_goal_group_correlation_uuid", "hs_influenced_closed_deal_goal", "hs_influenced_contact_goal", "hs_influenced_revenue", "hs_lastmodifieddate", "hs_name", "hs_new_contact_goal", "hs_notes", "hs_origin_asset_id", "hs_owner", "hs_projected_budget", "hs_revenue", "hs_session_goal", "hs_spend_amount", "hs_spend_items_sum_amount", "hs_start_date", "hs_template_guid", "hs_utm", "hs_supports_asset_placeholders", "hs_prompt_inputs"];
import PortalIdParser from 'PortalIdParser';
import CampaignRecord from '../data/CampaignRecord';
import { CAMPAIGN_PROPERTIES } from '../constants/crmCampaignProperties';
const CAMPAIGN_HUBSPOT_DEFINED_PROPERTIES = Object.values(CAMPAIGN_PROPERTIES);

/**
 *
 * CAMPAIGN RECORD => CRM OBJECT
 *
 * */
const crmObjectToRecordMap = {
  [CAMPAIGN_PROPERTIES.CREATED_AT]: 'createdAt',
  [CAMPAIGN_PROPERTIES.GOAL]: 'goal',
  [CAMPAIGN_PROPERTIES.GOAL_INTENTION]: 'goalIntention',
  [CAMPAIGN_PROPERTIES.GOAL_GROUP_CORRELATION_UUID]: 'goalGroupCorrelationUuid',
  [CAMPAIGN_PROPERTIES.CREATED_BY_USER_ID]: 'createdBy',
  [CAMPAIGN_PROPERTIES.NAME]: 'display_name',
  [CAMPAIGN_PROPERTIES.GUID]: 'guid',
  [CAMPAIGN_PROPERTIES.COLOR]: 'colorHex',
  [CAMPAIGN_PROPERTIES.BUDGET]: 'actualBudget',
  [CAMPAIGN_PROPERTIES.AUDIENCE]: 'audience',
  [CAMPAIGN_PROPERTIES.NOTES]: 'notes',
  [CAMPAIGN_PROPERTIES.END_DATE]: 'endedAt',
  [CAMPAIGN_PROPERTIES.OWNER]: 'owner',
  [CAMPAIGN_PROPERTIES.START_DATE]: 'startedAt',
  [CAMPAIGN_PROPERTIES.UTM]: 'utm',
  [CAMPAIGN_PROPERTIES.BUSINESS_UNIT_IDS]: 'businessUnitsInfo',
  [CAMPAIGN_PROPERTIES.CURRENCY_CODE]: 'currencyCode',
  [CAMPAIGN_PROPERTIES.BUDGET_AMOUNT]: 'numericBudget',
  [CAMPAIGN_PROPERTIES.SPEND_AMOUNT]: 'spendAmount',
  [CAMPAIGN_PROPERTIES.BUDGET_TOTAL_AMOUNT]: 'budgetTotalAmount',
  [CAMPAIGN_PROPERTIES.SPEND_TOTAL_AMOUNT]: 'spendTotalAmount',
  [CAMPAIGN_PROPERTIES.TEMPLATE_GUID]: 'templateGuid',
  [CAMPAIGN_PROPERTIES.SUPPORTS_ASSET_PLACEHOLDERS]: 'supportsAssetPlaceholders',
  [CAMPAIGN_PROPERTIES.STATUS]: 'status',
  [CAMPAIGN_PROPERTIES.ATTRIBUTION_ENABLED]: 'attributionEnabled',
  [CAMPAIGN_PROPERTIES.PROJECTED_BUDGET]: 'projectedBudget',
  [CAMPAIGN_PROPERTIES.REVENUE]: 'revenue',
  [CAMPAIGN_PROPERTIES.PROMPT_INPUTS]: 'promptInputs'
};
function formatPropertyValue(value) {
  if (typeof value === 'number' || typeof value === 'boolean') {
    return `${value}`;
  }
  return value;
}
function toCrmObjectProperties(propertyMap) {
  return Object.keys(propertyMap).reduce((acc, key) => Object.assign({}, acc, {
    [key]: {
      value: formatPropertyValue(propertyMap[key])
    }
  }), {});
}

/**
 * Returns a crm object from a campaign record.
 *
 * @param {CampaignRecord} campaignRecord
 */
export function toCampaignCrmObject(campaignRecord) {
  const baseProperties = toCrmObjectProperties(Object.entries(crmObjectToRecordMap).reduce((acc, [crmObjectProperty, recordProperty]) => Object.assign({}, acc, {
    [crmObjectProperty]: campaignRecord[recordProperty]
  }), {}));
  const campaignGoals = campaignRecord.goals || {
    contacts: null,
    customers: null,
    influencedContacts: null,
    influencedDeals: null,
    influencedRevenue: null,
    newContacts: null,
    sessions: null,
    visits: null
  };
  const campaignGoalsProperties = toCrmObjectProperties({
    hs_influenced_contact_goal: campaignGoals.influencedContacts != null ? String(campaignGoals.influencedContacts) : null,
    hs_influenced_closed_deal_goal: campaignGoals.influencedDeals != null ? String(campaignGoals.influencedDeals) : null,
    hs_influenced_revenue: campaignGoals.influencedRevenue != null ? String(campaignGoals.influencedRevenue) : null,
    hs_new_contact_goal: campaignGoals.newContacts != null ? String(campaignGoals.newContacts) : null,
    hs_session_goal: campaignGoals.sessions != null ? String(campaignGoals.sessions) : null
  });
  const customProperties = toCrmObjectProperties(campaignRecord.customProperties || {});
  const campaignCrmObject = {
    objectId: campaignRecord.objectId,
    objectTypeId: '0-35',
    properties: Object.assign({}, baseProperties, customProperties, campaignGoalsProperties),
    portalId: Number(PortalIdParser.get())
  };
  return campaignCrmObject;
}

/**
 *
 * CRM OBJECT => CAMPAIGN RESPONSE / RECORD
 *
 * */

function valueOrNull(property) {
  return property ? property.value : null;
}
function parseIntOrNull(property) {
  const value = valueOrNull(property);
  return value ? parseInt(value, 10) : null;
}
function safeParseJSONOrNull(json) {
  if (!json) {
    return null;
  }
  try {
    return JSON.parse(json);
  } catch (_unused) {
    return null;
  }
}
/**
 * Returns campaign response properties from crm object properties.
 */
export function fromCrmPropertiesToCampaignResponseProperties({
  properties
}, options) {
  const {
      hs_action_metadata,
      hs_all_assigned_business_unit_ids,
      hs_attribution_enabled,
      hs_audience,
      hs_ai_features,
      hs_budget_amount,
      hs_budget_items_sum_amount,
      hs_budget,
      hs_brief_properties,
      hs_campaign_status,
      hs_color_hex,
      hs_created_at,
      hs_created_by_user_id,
      hs_currency_code,
      hs_end_date,
      hs_goal,
      hs_goal_intention,
      hs_goal_group_correlation_uuid,
      hs_influenced_closed_deal_goal,
      hs_influenced_contact_goal,
      hs_influenced_revenue,
      hs_lastmodifieddate,
      hs_name,
      hs_new_contact_goal,
      hs_notes,
      hs_origin_asset_id,
      hs_owner,
      hs_projected_budget,
      hs_revenue,
      hs_session_goal,
      hs_spend_amount,
      hs_spend_items_sum_amount,
      hs_start_date,
      hs_template_guid,
      hs_utm,
      hs_supports_asset_placeholders,
      hs_prompt_inputs
    } = properties,
    restProperties = _objectWithoutPropertiesLoose(properties, _excluded);
  const businessUnit = parseIntOrNull(hs_all_assigned_business_unit_ids);
  const isValidCustomProperty = options && options.isValidCustomProperty || (() => true);
  const customProperties = restProperties ? Object.entries(restProperties).filter(([key]) => !CAMPAIGN_HUBSPOT_DEFINED_PROPERTIES.includes(key) && !key.startsWith('hs_') && isValidCustomProperty({
    name: key
  })).reduce((acc, [key, {
    value
  }]) => Object.assign({}, acc, {
    [key]: value
  }), {}) : undefined;
  return {
    actionMetadata: valueOrNull(hs_action_metadata),
    actualBudget: valueOrNull(hs_budget),
    attributionEnabled: valueOrNull(hs_attribution_enabled) === 'true',
    audience: valueOrNull(hs_audience),
    aiFeatures: safeParseJSONOrNull(valueOrNull(hs_ai_features)),
    budgetTotalAmount: parseIntOrNull(hs_budget_items_sum_amount),
    businessUnitsInfo: businessUnit != null ? {
      selectedBusinessUnits: [businessUnit]
    } : undefined,
    briefProperties: safeParseJSONOrNull(valueOrNull(hs_brief_properties)),
    colorHex: valueOrNull(hs_color_hex),
    createdAt: parseIntOrNull(hs_created_at),
    createdBy: parseIntOrNull(hs_created_by_user_id),
    currencyCode: valueOrNull(hs_currency_code),
    customProperties,
    display_name: valueOrNull(hs_name) || undefined,
    deleted: false,
    endedAt: parseIntOrNull(hs_end_date),
    goal: valueOrNull(hs_goal),
    goalIntention: valueOrNull(hs_goal_intention),
    goalGroupCorrelationUuid: valueOrNull(hs_goal_group_correlation_uuid),
    goals: {
      contacts: null,
      customers: null,
      influencedContacts: parseIntOrNull(hs_influenced_contact_goal),
      influencedDeals: parseIntOrNull(hs_influenced_closed_deal_goal),
      influencedRevenue: parseIntOrNull(hs_influenced_revenue),
      newContacts: parseIntOrNull(hs_new_contact_goal),
      sessions: parseIntOrNull(hs_session_goal),
      visits: null
    },
    guid: valueOrNull(hs_origin_asset_id),
    notes: valueOrNull(hs_notes),
    numericBudget: parseIntOrNull(hs_budget_amount),
    owner: parseIntOrNull(hs_owner),
    projectedBudget: valueOrNull(hs_projected_budget),
    revenue: parseIntOrNull(hs_revenue),
    spendAmount: parseIntOrNull(hs_spend_amount),
    spendTotalAmount: parseIntOrNull(hs_spend_items_sum_amount),
    steps: null,
    status: valueOrNull(hs_campaign_status),
    startedAt: parseIntOrNull(hs_start_date),
    templateGuid: valueOrNull(hs_template_guid),
    updatedAt: parseIntOrNull(hs_lastmodifieddate),
    utm: valueOrNull(hs_utm) || undefined,
    supportsAssetPlaceholders: valueOrNull(hs_supports_asset_placeholders) === 'true',
    promptInputs: valueOrNull(hs_prompt_inputs)
  };
}

/**
 * Returns a campaign response from a crm object.
 */
export function toCampaignResponse({
  properties,
  objectId,
  portalId
}, options) {
  const campaignResponseProperties = fromCrmPropertiesToCampaignResponseProperties({
    properties
  }, options);
  return Object.assign({}, campaignResponseProperties, {
    objectId,
    portalId
  });
}

/**
 * Returns a campaign record from a crm object
 */
export function toLegacyCampaignRecord(crmObject) {
  return CampaignRecord.from(toCampaignResponse(crmObject));
}